/**
 * ONE RULE for "This week / This month / This quarter / This year", on every
 * surface: the WHOLE calendar period — Monday to Sunday, the 1st to the last
 * day, the full quarter, the full year (IST calendar).
 *
 * Before 2026-10-05 the dashboard, the reports and the report builder ended
 * these presets at TODAY while the leads page ended them at the period's real
 * end. For anything dated in the past (created, moved, assigned, calls,
 * activity, payments collected) the two read the same — nothing exists after
 * today. They differed for anything that can be dated in the FUTURE
 * (follow-ups, due dates): "follow-ups this week" on the leads page included
 * Thursday's, the same words on a dashboard card did not.
 *
 * Two guards keep the period's real end from distorting what must stay
 * "so far":
 *   • COMPARE uses the ELAPSED part of the period (`elapsedWindow`): this week
 *     Monday-to-today against last week Monday-to-the-same-weekday. Comparing
 *     a part-week with a full week would turn every arrow red until Sunday.
 *   • RATES over things that fall due (follow-up compliance = done ÷ due)
 *     count what is due SO FAR — the server caps those at today, since a
 *     follow-up due on Friday cannot be "missed" on Wednesday.
 *
 * The week / month / year arithmetic is the leads page's own
 * (`getPresetDateRangeIST`), so the bars and the leads filter cannot drift.
 */
import { getPresetDateRangeIST, getTodayYmdIST } from "./istCalendar";

/** An inclusive calendar-day window, `yyyy-MM-dd` IST days. */
export interface YmdWindow {
  startDate: string;
  endDate: string;
}

export type ThisPeriodKey = "this_week" | "this_month" | "this_quarter" | "this_year";

const THIS_PERIODS: ReadonlySet<string> = new Set([
  "this_week",
  "this_month",
  "this_quarter",
  "this_year",
]);
export const isThisPeriod = (key: string): key is ThisPeriodKey => THIS_PERIODS.has(key);

const pad = (n: number) => String(n).padStart(2, "0");

/** The whole calendar period that contains today (IST). */
export function thisPeriodWindow(key: ThisPeriodKey, now: Date = new Date()): YmdWindow {
  if (key === "this_quarter") {
    const [y, m] = getTodayYmdIST(now).split("-").map(Number) as [number, number];
    const firstMonth = Math.floor((m - 1) / 3) * 3 + 1; // 1, 4, 7, 10
    const lastMonth = firstMonth + 2;
    const lastDay = new Date(Date.UTC(y, lastMonth, 0)).getUTCDate();
    return {
      startDate: `${y}-${pad(firstMonth)}-01`,
      endDate: `${y}-${pad(lastMonth)}-${pad(lastDay)}`,
    };
  }
  const { start, end } = getPresetDateRangeIST(key, now);
  return { startDate: start, endDate: end };
}

/**
 * The part of a window that has already happened: its end capped at today
 * (IST). Unchanged for a window that ended in the past; `null` for one that
 * has not started yet (nothing has elapsed — there is nothing to compare).
 */
export function elapsedWindow(window: YmdWindow, now: Date = new Date()): YmdWindow | null {
  const today = getTodayYmdIST(now);
  if (window.startDate > today) return null;
  return window.endDate > today ? { startDate: window.startDate, endDate: today } : window;
}
